import { useCallback } from "react"

import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import { DashboardFiltersBlock } from "../../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import DropdownBlockCheckbox from "../../../Components/Filters/DropdownBlockCheckbox"
import DropdownBlockTags from "../../../Components/Filters/DropdownBlockTags"
import { filtersTypeById } from "../../../Components/Filters/filtersTypeById"
import GlobalFilterTestData from "../../../Components/GlobalFilterTestData/globalFilterTest"
import { alertsColumnIconMap } from "../../../Utils/utils"
import Toggle from "../../DeflectAnalyticsPage/components/Toggle"
import { addGlobalFilter, removeGlobalFilter } from "../../GlobalFilters/redux/globalFilters.slice"
import { CHANNEL_OPTIONS, PROGRAM_OPTIONS } from "../constants"
import {
  changeAdminDeflectFilters,
  selectAdminDeflectFilters
} from "../redux/adminDeflectAnalytics.slice"
import { withCurrentPreset } from "../utils/period"

import styles from "../adminDeflectAnalytics.module.less"

const TAG_FILTER_IDS = ["user_email", "user_full_name", "business_account"]

// The row is right-aligned, so a trigger whose text grows widens to the left: its popup is anchored
// to the right edge, which stays put, instead of jumping with the left one.
const POPUP_PLACEMENT = "bottomRight"

// The filter row of Admin Analytics → Deflect, right-aligned, in the order of DAA-2-07: Program,
// Channels, the three tag filters, Date range, Compare, Test On / Test Off.
const AdminDeflectFilters = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const filters = useSelector(selectAdminDeflectFilters)
  const isMobile = useSelector((state) => state.nav.isMobile)

  const change = useCallback((patch) => dispatch(changeAdminDeflectFilters(patch)), [dispatch])

  // A custom range arrives as a bare [from, to]; stored the way the Alerts Dashboard stores it,
  // which is the shape the Compare control reads to tell a custom window from a relative one.
  const handleChangePeriod = useCallback(
    (patch) =>
      change(
        Array.isArray(patch.period)
          ? { ...patch, period: { type: "custom", period: patch.period } }
          : patch
      ),
    [change]
  )

  const handleChangeHide = useCallback(
    (key, isHidden) => {
      const otherKeys = (filters.exclude_fields ?? []).filter((field) => field !== key)
      change({ exclude_fields: isHidden ? [...otherKeys, key] : otherKeys })
    },
    [change, filters.exclude_fields]
  )

  // User email is the admin panel's global email filter (DAA-2-10): every change is written there too, as the
  // Alerts page does, so it holds even after the last tag was removed and the global key deleted. Removing the
  // last tag of a filter, here or on another page, also unticks its Hide (DAA-2-13).
  const handleChangeTags = useCallback(
    (patch) => {
      if (Array.isArray(patch.user_email)) {
        const emails = patch.user_email.filter(Boolean)

        if (emails.length) {
          emails.forEach((email) =>
            dispatch(addGlobalFilter({ key: "user_email", value: email.trim() }))
          )
        } else {
          dispatch(removeGlobalFilter("user_email"))
        }
      }

      const clearedIds = TAG_FILTER_IDS.filter(
        (id) => Array.isArray(patch[id]) && !patch[id].some(Boolean)
      )
      const hiddenIds = filters.exclude_fields ?? []

      change(
        clearedIds.some((id) => hiddenIds.includes(id))
          ? { ...patch, exclude_fields: hiddenIds.filter((id) => !clearedIds.includes(id)) }
          : patch
      )
    },
    [change, dispatch, filters.exclude_fields]
  )

  const handleAddGlobalFilter = useCallback(
    (key, value) => dispatch(addGlobalFilter({ key, value })),
    [dispatch]
  )

  const handleRemoveGlobalFilter = useCallback(
    (key) => dispatch(removeGlobalFilter(key)),
    [dispatch]
  )

  return (
    <Flex justify="flex-end" align="center" gap={12} wrap="wrap" className={styles.filterRow}>
      <Toggle
        value={filters.program}
        onChange={(program) => change({ program })}
        options={PROGRAM_OPTIONS}
      />
      <DropdownBlockCheckbox
        id="channels"
        label={t("Channels")}
        iconType="type"
        allBtnText="All"
        data={CHANNEL_OPTIONS}
        type="chart"
        sortDisabled
        dropdownFilters={filters.channels}
        setDropdownFilters={change}
        placement={POPUP_PLACEMENT}
      />
      {TAG_FILTER_IDS.map((id) => (
        <DropdownBlockTags
          key={id}
          {...filtersTypeById[id]}
          iconType={alertsColumnIconMap[id]}
          dropdownFilters={filters[id]}
          setDropdownFilters={handleChangeTags}
          valueHide={filters.exclude_fields?.includes(id)}
          onChangeHide={handleChangeHide}
          addGlobalFilter={handleAddGlobalFilter}
          removeGlobalFilter={handleRemoveGlobalFilter}
          isMobile={isMobile}
          placement={POPUP_PLACEMENT}
        />
      ))}
      <DashboardFiltersBlock
        onlyPeriod
        showCompare
        filters={withCurrentPreset(filters)}
        handleChangeFilter={handleChangePeriod}
        placement={POPUP_PLACEMENT}
      />
      {/* The platform's Test On / Test Off, as on Marketing: the selected option white on the grey
          track (CHA1-4578). Large, the 40px of the other filters. */}
      <GlobalFilterTestData size="large" />
    </Flex>
  )
}

export default AdminDeflectFilters
